import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, PlusIcon, SearchIcon, XIcon } from 'lucide-react';
import { VOCABULAIRE_GROEPEN, type VocabulaireGroep, type VocabulaireTerm } from '@vip/shared';

import { Alert, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '../../lib/apiClient';
import { useBewaarVocabulaire, useVocabulaire } from './vocabulaire.api';

/**
 * De vaktermen die de transcriptie en het samenvatten meekrijgen.
 *
 * Zonder deze lijst gokt het transcriptiemodel: in een echte doorloop verzon het
 * "Nautilus" en "iASSET" als uitbreiding van namen die het half verstond. Met
 * lijst heeft het iets om tegen aan te leggen, en de samenvatting neemt de
 * schrijfwijze over.
 *
 * Hier stond ook een lijst met bekende verhaspelingen: "F mecca" hoort "FMECA"
 * te worden. Die stuurde de correctiepas aan, en die pas bestaat niet meer; het
 * model leidt de juiste schrijfwijze zelf af uit de termenlijst. Op een echt
 * gesprek bleef P&ID overal correct zonder dat er één correctieregel aan te pas
 * kwam. De verhaspelingen staan nog wel in vocabulaire/vaktermen.json, mocht
 * blijken dat het model ze toch nodig heeft.
 *
 * Alles wordt in één keer opgeslagen, als nieuwe versie in de instellingenketen
 * (§6.1), zodat elke wijziging één stap terug te draaien is.
 */
export function VocabulairePagina() {
  const { data, isLoading } = useVocabulaire();
  const bewaar = useBewaarVocabulaire();

  const [termen, setTermen] = useState<VocabulaireTerm[]>([]);
  const [zoek, setZoek] = useState('');
  const [notitie, setNotitie] = useState('');
  const [fout, setFout] = useState('');
  const [bewaard, setBewaard] = useState('');

  useEffect(() => {
    if (data) setTermen(data.termen);
  }, [data]);

  const gewijzigd = !!data && JSON.stringify(termen) !== JSON.stringify(data.termen);

  const naar = zoek.trim().toLowerCase();
  const raakt = (s: string) => Boolean(naar) && s.toLowerCase().includes(naar);

  const perGroep = useMemo(() => {
    const uit = new Map<string, VocabulaireTerm[]>();
    for (const g of VOCABULAIRE_GROEPEN) uit.set(g.id, []);
    for (const t of termen) uit.get(t.groep)?.push(t);
    return uit;
  }, [termen]);

  const treffers = naar ? termen.filter((t) => raakt(t.term)).length : 0;

  function voegToe(term: string, groep: VocabulaireGroep) {
    const t = term.trim();
    if (!t || termen.some((x) => x.term.toLowerCase() === t.toLowerCase())) return;
    setTermen([...termen, { term: t, groep }]);
  }

  async function opslaan() {
    setFout('');
    setBewaard('');
    try {
      const uit = await bewaar.mutateAsync({ termen, changeNote: notitie.trim() });
      setNotitie('');
      setBewaard(`Opgeslagen als versie ${uit.versie}.`);
    } catch (err) {
      setFout(err instanceof ApiError ? err.message : String(err));
    }
  }

  if (isLoading) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Vaktermen</CardTitle>
          <CardDescription className="tabular-nums">
            {termen.length} termen · versie {data?.versie ?? '—'}
          </CardDescription>
          <CardAction>
            <InputGroup className="w-full sm:w-64">
              <InputGroupAddon>
                <SearchIcon />
              </InputGroupAddon>
              <InputGroupInput
                aria-label="Zoeken in de vaktermen"
                onChange={(e) => setZoek(e.target.value)}
                placeholder="Zoek een vakterm"
                value={zoek}
              />
              {naar && (
                <InputGroupAddon align="inline-end">
                  <InputGroupButton aria-label="Zoekterm wissen" onClick={() => setZoek('')}>
                    <XIcon />
                  </InputGroupButton>
                </InputGroupAddon>
              )}
            </InputGroup>
          </CardAction>
        </CardHeader>

        <CardContent className="flex flex-col gap-6">
          {naar && (
            <p className="text-sm text-muted-foreground" role="status">
              {treffers === 0 ? `Niets gevonden voor “${zoek}”.` : `${treffers} gevonden`}
            </p>
          )}

          {VOCABULAIRE_GROEPEN.map((g) => {
            const alles = perGroep.get(g.id) ?? [];
            // Tijdens het zoeken alleen de treffers, niet de hele groep waar er
            // toevallig één in zit: dan lees je alsnog langs wat je niet zocht.
            const inGroep = naar ? alles.filter((t) => raakt(t.term)) : alles;
            if (naar && inGroep.length === 0) return null;
            if (!naar && alles.length === 0 && g.id === 'overig') return null;
            return (
              <div className="flex flex-col gap-2" key={g.id}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{g.label}</span>
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {naar ? `${inGroep.length} van ${alles.length}` : alles.length}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {inGroep.map((t) => (
                    // De hele badge is de wisknop, met het kruisje als teken. Dat
                    // mag, want niets wordt bewaard tot je op opslaan drukt en de
                    // balk onderaan biedt ongedaan maken.
                    <Badge asChild key={t.term} variant={raakt(t.term) ? 'default' : 'secondary'}>
                      <button
                        aria-label={`${t.term} verwijderen`}
                        onClick={() => setTermen(termen.filter((x) => x.term !== t.term))}
                        type="button"
                      >
                        {t.term}
                        <XIcon data-icon="inline-end" />
                      </button>
                    </Badge>
                  ))}
                  {!naar && <Toevoegen onToevoegen={(term) => voegToe(term, g.id)} />}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {fout && (
        <Alert variant="destructive">
          <AlertTitle>{fout}</AlertTitle>
        </Alert>
      )}
      {bewaard && !gewijzigd && (
        <Alert>
          <CheckIcon />
          <AlertTitle>{bewaard}</AlertTitle>
        </Alert>
      )}

      {/* De opslagbalk komt pas in beeld als er iets te bewaren valt, en blijft
          dan staan: bij een lange lijst scrol je ver van een knop onderaan de
          pagina weg. */}
      {gewijzigd && (
        <Card className="sticky bottom-4 ring-primary/30">
          <CardContent className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium">Niet opgeslagen wijzigingen</span>
            <Input
              aria-label="Toelichting bij deze versie"
              className="w-full sm:w-64"
              onChange={(e) => setNotitie(e.target.value)}
              placeholder="Wat is er veranderd?"
              value={notitie}
            />
            <div className="ml-auto flex items-center gap-2">
              <Button
                onClick={() => {
                  setTermen(data?.termen ?? []);
                  setNotitie('');
                }}
                variant="ghost"
              >
                Ongedaan maken
              </Button>
              <Button disabled={bewaar.isPending || termen.length === 0} onClick={opslaan}>
                {bewaar.isPending && <Spinner data-icon="inline-start" />}
                {bewaar.isPending ? 'Bezig…' : 'Opslaan'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** Een plusje dat een invoerveld wordt. Klein zolang je het niet nodig hebt. */
function Toevoegen({ onToevoegen }: { onToevoegen: (waarde: string) => void }) {
  const [open, setOpen] = useState(false);
  const [waarde, setWaarde] = useState('');
  const veld = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) veld.current?.focus();
  }, [open]);

  if (!open) {
    return (
      <Badge asChild variant="outline">
        <button onClick={() => setOpen(true)} type="button">
          <PlusIcon data-icon="inline-start" />
          term
        </button>
      </Badge>
    );
  }

  return (
    <Input
      aria-label="Nieuwe vakterm"
      className="h-6 w-40"
      onBlur={() => {
        if (waarde.trim()) onToevoegen(waarde);
        setWaarde('');
        setOpen(false);
      }}
      onChange={(e) => setWaarde(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          // Doorgaan met de volgende: wie één term toevoegt, voegt er meestal
          // meer toe.
          if (waarde.trim()) onToevoegen(waarde);
          setWaarde('');
        }
        if (e.key === 'Escape') {
          setWaarde('');
          setOpen(false);
        }
      }}
      ref={veld}
      value={waarde}
    />
  );
}
